import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { engineApi, type AssetWritePayload, type MarkAssetSoldPayload } from "../services/engineApi";

export const engineKeys = {
  all: ["engines"] as const,
  lists: () => [...engineKeys.all, "list"] as const,
  list: (filters: Record<string, any>) => [...engineKeys.lists(), filters] as const,
  details: () => [...engineKeys.all, "detail"] as const,
  detail: (id: string) => [...engineKeys.details(), id] as const,
};

/**
 * Fetch the engine list. CRITICAL: pass a MEMOIZED filters object from the
 * caller (useMemo) to avoid infinite re-renders, mirroring useBoats.
 */
export function useEngines(filters: Record<string, any>, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: engineKeys.list(filters),
    queryFn: () => engineApi.getEngines(filters),
    enabled: options?.enabled ?? true,
  });
}

/** Fetch a single engine (+ tagged-boat history), mirroring useBoat. */
export function useEngine(id: string) {
  return useQuery({
    queryKey: engineKeys.detail(id),
    queryFn: () => engineApi.getEngine(id),
    enabled: !!id,
  });
}

/**
 * SP2-D mutations. Deleting an attached engine also mutates its boat
 * (the active link is closed server-side), so boat queries invalidate too.
 */
function useEngineWriteInvalidation() {
  const queryClient = useQueryClient();

  return (id?: string) => {
    queryClient.invalidateQueries({ queryKey: engineKeys.lists() });
    if (id) queryClient.invalidateQueries({ queryKey: engineKeys.detail(id) });
    queryClient.invalidateQueries({ queryKey: ["boats"] });
  };
}

export function useCreateEngine() {
  const invalidate = useEngineWriteInvalidation();

  return useMutation({
    mutationFn: (data: AssetWritePayload) => engineApi.createEngine(data),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateEngine(id: string) {
  const invalidate = useEngineWriteInvalidation();

  return useMutation({
    mutationFn: (data: AssetWritePayload) => engineApi.updateEngine(id, data),
    onSuccess: () => invalidate(id),
  });
}

export function useDeleteEngine() {
  const invalidate = useEngineWriteInvalidation();

  return useMutation({
    mutationFn: (id: string) => engineApi.deleteEngine(id),
    onSuccess: () => invalidate(),
  });
}

/** SP4 slice 1 — mark-sold. 422s with errors.active_link while attached (D2). */
export function useMarkEngineSold(id: string) {
  const invalidate = useEngineWriteInvalidation();

  return useMutation({
    mutationFn: (data: MarkAssetSoldPayload) => engineApi.markSold(id, data),
    onSuccess: () => invalidate(id),
  });
}
